import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { toast } from "sonner";
import { logError } from "@/lib/errorLogger";
import { ACTIVE_MEETING_STATUSES, SCHEDULED_MEETING_STATUSES } from "@/lib/meetingStatus";

export interface MeetingProposal {
  id: string;
  meeting_id: string;
  proposed_by: string;
  note: string | null;
  status: "open" | "accepted" | "expired" | "cancelled";
  created_at: string;
  updated_at: string;
}

export interface ProposalSlot {
  id: string;
  proposal_id: string;
  slot_start_at: string;
  slot_end_at: string;
  is_selected: boolean;
  created_at: string;
}

export interface MeetingJoinEvent {
  id: string;
  meeting_id: string;
  user_id: string;
  joined_at: string;
  source: string | null;
}

export interface ScheduledMeetingWithDetails {
  id: string;
  thread_id: string;
  mentorship_request_id: string;
  mentor_id: string | null;
  entrepreneur_id: string | null;
  proposed_by: string;
  proposed_duration: number;
  proposed_note: string | null;
  status: string;
  scheduled_start_at: string | null;
  scheduled_end_at: string | null;
  timezone: string;
  duration_minutes: number;
  zoom_meeting_id: string | null;
  zoom_join_url: string | null;
  zoom_start_url: string | null;
  cancelled_at: string | null;
  cancelled_by: string | null;
  cancellation_reason: string | null;
  calendar_uid: string | null;
  calendar_sequence: number;
  last_invite_sent_at: string | null;
  last_invite_status: string | null;
  created_at: string;
  updated_at: string;
  proposals?: MeetingProposal[];
}

// Fetch scheduled meeting for a thread (slot-based meetings only)
// Immediate "Zoom Now" meetings are now in a separate table (immediate_meetings)
// and handled by useImmediateMeetings.ts
export function useScheduledMeeting(threadId?: string) {
  return useQuery({
    queryKey: ["scheduled-meeting", threadId],
    queryFn: async () => {
      if (!threadId) return null;

      // Step 1: Get meetings for this thread (without join to avoid RLS issues)
      const { data: meetings, error: meetingsError } = await supabase
        .from("mentorship_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .in("status", ACTIVE_MEETING_STATUSES)
        .order("created_at", { ascending: false });

      if (meetingsError) throw meetingsError;
      if (!meetings || meetings.length === 0) return null;

      // Step 2: Get OPEN proposals for these meetings (separate query for proper RLS)
      // Only count open proposals - cancelled/expired proposals should not keep the meeting visible
      const meetingIds = meetings.map(m => m.id);
      const { data: proposals, error: proposalsError } = await supabase
        .from("mentorship_meeting_proposals")
        .select("id, meeting_id")
        .in("meeting_id", meetingIds)
        .eq("status", "open");

      if (proposalsError) {
        console.warn("[useScheduledMeeting] Failed to fetch proposals:", proposalsError);
        // Continue without proposal data - will fall back to status-based logic
      }

      // Create a map of meeting_id -> has proposals
      const meetingHasProposals = new Map<string, boolean>();
      if (proposals) {
        for (const p of proposals) {
          meetingHasProposals.set(p.meeting_id, true);
        }
      }

      console.log("[useScheduledMeeting] Open proposal check:", {
        meetingCount: meetings.length,
        openProposalCount: proposals?.length || 0,
        meetingsWithOpenProposals: Array.from(meetingHasProposals.keys()),
      });

      // Find a scheduled meeting (slot-based only)
      // Now that immediate meetings are in a separate table, this is simpler:
      // - status = 'scheduled' (confirmed slot-based meeting), OR
      // - status = 'proposed' AND has proposals attached (pending slot-based proposal)
      const scheduledMeeting = meetings.find(m => {
        if (m.status === 'scheduled') {
          return true; // All scheduled meetings in this table are slot-based
        }
        
        // For proposed meetings, check if they have OPEN proposals
        // If all proposals are cancelled, don't show this meeting
        const hasOpenProposals = meetingHasProposals.get(m.id) === true;
        console.log(`[useScheduledMeeting] Meeting ${m.id}: status=${m.status}, hasOpenProposals=${hasOpenProposals}`);
        return hasOpenProposals;
      });

      if (!scheduledMeeting) {
        console.log("[useScheduledMeeting] No TRUE scheduled meeting found");
        return null;
      }

      console.log("[useScheduledMeeting] Found scheduled meeting:", scheduledMeeting.id);
      return scheduledMeeting as ScheduledMeetingWithDetails;
    },
    enabled: !!threadId,
    // Poll faster when waiting for Zoom link, otherwise standard interval
    refetchInterval: (query) => {
      const meeting = query.state.data as ScheduledMeetingWithDetails | null;
      // Poll every 3 seconds when meeting is scheduled but missing Zoom link
      if (meeting?.status === "scheduled" && !meeting?.zoom_join_url) {
        return 3000;
      }
      return 10000; // Standard 10s polling
    },
  });
}

// Fetch open proposals for a meeting
export function useOpenProposals(meetingId?: string) {
  return useQuery({
    queryKey: ["meeting-proposals", meetingId],
    queryFn: async () => {
      if (!meetingId) return [];

      const { data, error } = await supabase
        .from("mentorship_meeting_proposals")
        .select("*")
        .eq("meeting_id", meetingId)
        .eq("status", "open")
        .order("created_at", { ascending: false });

      if (error) throw error;
      return data as MeetingProposal[];
    },
    enabled: !!meetingId,
  });
}

// Fetch slots for a proposal
export function useProposalSlots(proposalId?: string) {
  return useQuery({
    queryKey: ["proposal-slots", proposalId],
    queryFn: async () => {
      if (!proposalId) return [];

      const { data, error } = await supabase
        .from("mentorship_meeting_proposal_slots")
        .select("*")
        .eq("proposal_id", proposalId)
        .order("slot_start_at", { ascending: true });

      if (error) throw error;
      return data as ProposalSlot[];
    },
    enabled: !!proposalId,
  });
}

// Fetch all open proposals with their slots for a thread
// CRITICAL: This hook fetches proposals + slots in a single query to avoid cache race conditions
export function useOpenProposalsWithSlots(threadId?: string) {
  return useQuery({
    queryKey: ["open-proposals-with-slots", threadId],
    queryFn: async () => {
      if (!threadId) return [];

      console.log("[useOpenProposalsWithSlots] Starting fetch for thread:", threadId);

      try {
        // First get the meeting
        const { data: meeting, error: meetingError } = await supabase
          .from("mentorship_meetings")
          .select("id")
          .eq("thread_id", threadId)
          .in("status", ACTIVE_MEETING_STATUSES)
          .order("created_at", { ascending: false })
          .limit(1)
          .maybeSingle();

        if (meetingError) {
          console.error("[useOpenProposalsWithSlots] Meeting query error:", meetingError);
          throw meetingError;
        }

        if (!meeting) {
          console.log("[useOpenProposalsWithSlots] No meeting found for thread");
          return [];
        }

        console.log("[useOpenProposalsWithSlots] Found meeting:", meeting.id);

        // Get open proposals for this meeting
        const { data: proposals, error: proposalsError } = await supabase
          .from("mentorship_meeting_proposals")
          .select("*")
          .eq("meeting_id", meeting.id)
          .eq("status", "open")
          .order("created_at", { ascending: false });

        if (proposalsError) {
          console.error("[useOpenProposalsWithSlots] Proposals query error:", proposalsError);
          throw proposalsError;
        }

        if (!proposals || proposals.length === 0) {
          console.log("[useOpenProposalsWithSlots] No open proposals found");
          return [];
        }

        console.log("[useOpenProposalsWithSlots] Found proposals:", proposals.length, proposals.map(p => p.id));

        // Get slots for all proposals
        const proposalIds = proposals.map((p) => p.id);
        const { data: slots, error: slotsError } = await supabase
          .from("mentorship_meeting_proposal_slots")
          .select("*")
          .in("proposal_id", proposalIds)
          .order("slot_start_at", { ascending: true });

        if (slotsError) {
          console.error("[useOpenProposalsWithSlots] Slots query error:", slotsError);
          throw slotsError;
        }

        console.log("[useOpenProposalsWithSlots] Found slots:", slots?.length || 0, slots?.map(s => s.id) || []);

        // Combine proposals with their slots
        const result = proposals.map((proposal) => ({
          ...proposal,
          slots: (slots || []).filter((s) => s.proposal_id === proposal.id),
        }));

        console.log("[useOpenProposalsWithSlots] Final result:", {
          proposalsCount: result.length,
          slotsPerProposal: result.map(p => ({ id: p.id, slotCount: p.slots.length })),
        });

        // VALIDATION: Log warning if slots exist in DB but not fetched
        if (proposals.length > 0 && (!slots || slots.length === 0)) {
          console.warn("[useOpenProposalsWithSlots] WARNING: Proposals found but no slots - possible RLS issue");
          await logError({
            error: "Proposals found but slots query returned empty",
            component: "useOpenProposalsWithSlots",
            action: "slot_fetch_empty",
            metadata: {
              thread_id: threadId,
              meeting_id: meeting.id,
              proposal_ids: proposalIds,
            }
          });
        }

        return result;
      } catch (error) {
        console.error("[useOpenProposalsWithSlots] Query failed:", error);
        await logError({
          error: error instanceof Error ? error : String(error),
          component: "useOpenProposalsWithSlots",
          action: "query_failed",
          metadata: { thread_id: threadId }
        });
        throw error;
      }
    },
    enabled: !!threadId,
    staleTime: 1000, // Consider stale after 1 second (not 0 to avoid race conditions)
    gcTime: 30000, // Keep in cache for 30 seconds (gcTime: 0 causes race conditions)
    refetchOnMount: "always", // Always refetch when component mounts
    refetchInterval: 5000, // Aggressive polling (5s) to catch new slots
  });
}

// Create a meeting proposal with time slots
// Returns { meeting_id, proposal_id } on success for post-creation verification
export function useCreateScheduledMeetingProposal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      mentorshipRequestId,
      threadId,
      durationMinutes,
      timezone,
      note,
      slots,
    }: {
      mentorshipRequestId: string;
      threadId: string;
      durationMinutes: number;
      timezone: string;
      note?: string;
      slots: { start_at: string; end_at: string }[];
    }): Promise<{ meeting_id: string | null; proposal_id: string | null; slots_inserted: number }> => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      if (!slots || slots.length === 0) {
        throw new Error("At least one time slot is required");
      }

      console.log("[useCreateScheduledMeetingProposal] Calling RPC with:", {
        p_mentorship_request_id: mentorshipRequestId,
        p_thread_id: threadId,
        p_duration_minutes: durationMinutes,
        p_timezone: timezone,
        p_note: note || null,
        p_slots_count: slots.length,
      });

      const { data, error } = await supabase.rpc("create_scheduled_meeting_proposal", {
        p_mentorship_request_id: mentorshipRequestId,
        p_thread_id: threadId,
        p_duration_minutes: durationMinutes,
        p_timezone: timezone,
        p_note: note || null,
        p_slots: slots,
      });

      if (error) {
        console.error("[useCreateScheduledMeetingProposal] RPC error:", error);
        // Re-throw with more context
        throw new Error(`${error.code || "RPC_ERROR"}: ${error.message}`);
      }

      console.log("[useCreateScheduledMeetingProposal] RPC returned:", data);
      
      // RPC now returns jsonb: { meeting_id, proposal_id, slots_inserted }
      const responseData = data as { meeting_id?: string; proposal_id?: string; slots_inserted?: number } | null;
      const meetingId = responseData?.meeting_id || null;
      const proposalId = responseData?.proposal_id || null;
      const slotsInserted = responseData?.slots_inserted || 0;

      console.log("[useCreateScheduledMeetingProposal] Success:", { 
        meeting_id: meetingId, 
        proposal_id: proposalId,
        slots_inserted: slotsInserted 
      });
      
      return { 
        meeting_id: meetingId, 
        proposal_id: proposalId,
        slots_inserted: slotsInserted
      };
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["pending-meeting-proposal", variables.threadId] });
      toast.success("Meeting times proposed!");

      // Fire-and-forget: notify entrepreneur via email
      supabase.functions
        .invoke("send-meeting-proposal-notification", {
          body: { mentorship_request_id: variables.mentorshipRequestId },
        })
        .then(({ error }) => {
          if (error) {
            console.error("[useCreateScheduledMeetingProposal] Email notification failed:", error);
          } else {
            console.log("[useCreateScheduledMeetingProposal] Email notification sent");
          }
        });
    },
    onError: (error) => {
      console.error("[useCreateScheduledMeetingProposal] Mutation error:", error);
      // Toast is now handled by the calling component for more control
    },
  });
}

// Background function for Zoom creation (runs after accept, non-blocking)
// Records status to scheduling_debug_events for full pipeline observability
async function createZoomInBackground(
  meetingId: string,
  slotId: string,
  duration: number,
  queryClient: ReturnType<typeof useQueryClient>,
  threadId: string,
  mentorshipRequestId?: string
): Promise<{ success: boolean; error?: string; join_url?: string }> {
  const correlationId = `zoom_bg_${meetingId}_${Date.now()}`;
  
  try {
    console.log("[createZoomInBackground] Starting Zoom creation for meeting:", meetingId);
    
    // Log pipeline step: edge_invoke_start (non-blocking)
    void (async () => {
      try {
        await supabase.from("scheduling_debug_events").insert({
          stage: "edge_invoke_start",
          thread_id: threadId,
          meeting_id: meetingId,
          mentorship_request_id: mentorshipRequestId || null,
          slot_id: slotId,
          payload: { correlation_id: correlationId, duration },
        });
      } catch {}
    })();
    
    // Call edge function to create Zoom with a hard 25-second timeout
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 25000);
    
    let zoomResponse: { data?: { meeting_id?: string; join_url?: string; start_url?: string; resolved_start_at?: string }; error?: Error | null };
    
    try {
      zoomResponse = await supabase.functions.invoke("create-zoom-meeting", {
        body: {
          duration,
          slot_id: slotId,
          meeting_id: meetingId,
          mentorship_request_id: mentorshipRequestId,
        },
      });
    } catch (invokeErr) {
      if (invokeErr instanceof Error && invokeErr.name === 'AbortError') {
        throw new Error("TIMEOUT: Zoom creation took too long (>25s)");
      }
      throw invokeErr;
    } finally {
      clearTimeout(timeoutId);
    }

    if (zoomResponse.error) {
      console.error("[createZoomInBackground] Zoom creation failed:", zoomResponse.error);
      
      // Log pipeline step: edge_invoke_failed (non-blocking)
      void (async () => {
        try {
          await supabase.from("scheduling_debug_events").insert({
            stage: "edge_invoke_failed",
            thread_id: threadId,
            meeting_id: meetingId,
            slot_id: slotId,
            payload: { 
              correlation_id: correlationId,
              error: zoomResponse.error.message || String(zoomResponse.error),
            },
          });
        } catch {}
      })();
      
      await supabase.from("error_logs").insert({
        error_message: "Background Zoom creation failed",
        error_details: JSON.stringify(zoomResponse.error),
        component: "createZoomInBackground",
        action: "create_zoom_meeting",
        metadata: { meeting_id: meetingId, slot_id: slotId, correlation_id: correlationId },
      });
      
      return { success: false, error: zoomResponse.error.message || "Edge function failed" };
    }

    console.log("[createZoomInBackground] Zoom created, updating meeting with links");

    // Update the meeting with Zoom details
    const { error: updateError } = await supabase
      .from("mentorship_meetings")
      .update({
        zoom_meeting_id: zoomResponse.data?.meeting_id || null,
        zoom_join_url: zoomResponse.data?.join_url || null,
        zoom_start_url: zoomResponse.data?.start_url || null,
      })
      .eq("id", meetingId);

    if (updateError) {
      console.error("[createZoomInBackground] Failed to update meeting with Zoom links:", updateError);
      
      // Log pipeline step: db_update_failed (non-blocking)
      void (async () => {
        try {
          await supabase.from("scheduling_debug_events").insert({
            stage: "db_update_failed",
            thread_id: threadId,
            meeting_id: meetingId,
            slot_id: slotId,
            payload: { 
              correlation_id: correlationId,
              error: updateError.message,
              zoom_meeting_id: zoomResponse.data?.meeting_id,
            },
          });
        } catch {}
      })();
      
      await supabase.from("error_logs").insert({
        error_message: "Failed to update meeting with Zoom links",
        error_details: updateError.message,
        component: "createZoomInBackground",
        action: "update_meeting_zoom",
        metadata: { meeting_id: meetingId, correlation_id: correlationId },
      });
      
      return { success: false, error: "Failed to save Zoom details" };
    }

    console.log("[createZoomInBackground] Meeting updated with Zoom links");

    // Log pipeline step: zoom_complete (non-blocking)
    void (async () => {
      try {
        await supabase.from("scheduling_debug_events").insert({
          stage: "zoom_complete",
          thread_id: threadId,
          meeting_id: meetingId,
          slot_id: slotId,
          payload: { 
            correlation_id: correlationId,
            zoom_meeting_id: zoomResponse.data?.meeting_id,
            has_join_url: !!zoomResponse.data?.join_url,
          },
        });
      } catch {}
    })();

    // Invalidate queries so UI picks up the Zoom link
    queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId] });

    // Send calendar invite for future meetings (fire-and-forget)
    const resolvedStartAt = zoomResponse.data?.resolved_start_at;
    if (resolvedStartAt) {
      const scheduledStart = new Date(resolvedStartAt);
      const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
      
      if (scheduledStart >= fiveMinutesFromNow) {
        console.log("[createZoomInBackground] Sending calendar invite");
        supabase.functions.invoke("send-calendar-invite", {
          body: { meetingId, action: "create" },
        }).catch(inviteErr => {
          console.error("[createZoomInBackground] Calendar invite exception:", inviteErr);
        });
      }
    }
    
    return { success: true, join_url: zoomResponse.data?.join_url };
  } catch (err) {
    console.error("[createZoomInBackground] Exception:", err);
    
    // Log pipeline step: zoom_exception (non-blocking)
    void (async () => {
      try {
        await supabase.from("scheduling_debug_events").insert({
          stage: "zoom_exception",
          thread_id: threadId,
          meeting_id: meetingId,
          slot_id: slotId,
          payload: { 
            correlation_id: correlationId,
            error: err instanceof Error ? err.message : String(err),
          },
        });
      } catch {}
    })();
    
    await supabase.from("error_logs").insert({
      error_message: "Background Zoom creation exception",
      error_details: err instanceof Error ? err.message : String(err),
      component: "createZoomInBackground",
      action: "create_zoom_meeting",
      metadata: { meeting_id: meetingId, slot_id: slotId, correlation_id: correlationId },
    });
    
    return { success: false, error: err instanceof Error ? err.message : "Unknown error" };
  }
}

// Accept a time slot - ATOMIC + IDEMPOTENT via RPC
// IMPORTANT: For scheduled meetings, the slot_id is the ONLY source of truth for meeting time.
// This mutation IMMEDIATELY accepts the slot via atomic RPC, then creates Zoom in background.
// The RPC handles all validations, idempotency, and state transitions atomically.
export function useAcceptMeetingSlot() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      slotId,
      threadId,
      duration,
      meetingId,
      mentorshipRequestId,
    }: {
      slotId: string;
      threadId: string;
      duration: number;
      meetingId?: string;
      mentorshipRequestId?: string;
    }) => {
      // Generate correlation_id for tracking this accept flow
      const correlationId = `accept_${slotId}_${Date.now()}`;
      
      // Validate slot_id is required for scheduled meetings
      if (!slotId) {
        throw new Error("slot_id is required for scheduled meetings");
      }

      console.log("[useAcceptMeetingSlot] Starting atomic accept:", { correlationId, slotId });

      // Log accept_slot debug event (NON-BLOCKING) with version marker
      void supabase.from("scheduling_debug_events").insert({
        stage: "accept_slot_atomic_v2",
        user_id: user?.id || null,
        thread_id: threadId || null,
        meeting_id: meetingId || null,
        mentorship_request_id: mentorshipRequestId || null,
        slot_id: slotId,
        payload: { 
          duration, 
          correlation_id: correlationId, 
          note: "Atomic RPC accept - immediate Zoom kickoff",
          code_version: "2026.01.13.1"
        },
      });

      // STEP 1: Accept the slot via atomic RPC (idempotent, transaction-safe)
      // The RPC handles: participant validation, idempotency, state transitions, logging
      const { data: acceptedMeetingId, error } = await supabase.rpc("accept_meeting_slot", {
        p_slot_id: slotId,
      });

      if (error) {
        console.error("[useAcceptMeetingSlot] accept_meeting_slot RPC failed:", error);
        throw error;
      }

      console.log("[useAcceptMeetingSlot] Meeting accepted atomically! meetingId:", acceptedMeetingId);

      // STEP 2: IMMEDIATE Zoom kickoff with 10s fast-path SLA
      // Race between Zoom creation and timeout for responsive UI
      if (acceptedMeetingId && typeof acceptedMeetingId === "string") {
        const zoomPromise = supabase.functions.invoke("create-zoom-meeting", {
          body: {
            duration,
            slot_id: slotId,
            meeting_id: acceptedMeetingId,
            mentorship_request_id: mentorshipRequestId,
          },
        });

        // 10-second fast path - if Zoom completes quickly, great!
        // Otherwise, let it continue in background
        const timeoutPromise = new Promise<{ data: null; error: Error }>((resolve) =>
          setTimeout(() => resolve({ data: null, error: new Error("TIMEOUT") }), 10000)
        );

        try {
          const result = await Promise.race([zoomPromise, timeoutPromise]);
          
          if (result.error) {
            if (result.error.message === "TIMEOUT") {
              console.log("[useAcceptMeetingSlot] Zoom still creating in background (>10s)");
              // Continue in background - the hook's createZoomInBackground handles this
              void createZoomInBackground(
                acceptedMeetingId,
                slotId,
                duration,
                queryClient,
                threadId,
                mentorshipRequestId
              );
              return { meetingId: acceptedMeetingId, threadId, zoomReady: false };
            }
            console.error("[useAcceptMeetingSlot] Zoom creation failed:", result.error);
            // Still return success - meeting is scheduled, Zoom can be retried
            return { meetingId: acceptedMeetingId, threadId, zoomReady: false };
          }

          console.log("[useAcceptMeetingSlot] Zoom created in fast path!");
          
          // Update meeting with Zoom details
          if (result.data?.join_url) {
            await supabase
              .from("mentorship_meetings")
              .update({
                zoom_meeting_id: result.data.meeting_id || null,
                zoom_join_url: result.data.join_url,
                zoom_start_url: result.data.start_url || null,
                updated_at: new Date().toISOString(),
              })
              .eq("id", acceptedMeetingId);

            // Trigger calendar invite for future meetings (fire-and-forget)
            const scheduledStart = result.data.resolved_start_at ? new Date(result.data.resolved_start_at) : null;
            const fiveMinutesFromNow = new Date(Date.now() + 5 * 60 * 1000);
            
            if (scheduledStart && scheduledStart >= fiveMinutesFromNow) {
              console.log("[useAcceptMeetingSlot] Triggering calendar invite");
              void supabase.functions.invoke("send-calendar-invite", {
                body: { meetingId: acceptedMeetingId, action: "create" },
              });
            }
          }

          return { meetingId: acceptedMeetingId, threadId, zoomReady: true };
        } catch (err) {
          console.error("[useAcceptMeetingSlot] Zoom race error:", err);
          return { meetingId: acceptedMeetingId, threadId, zoomReady: false };
        }
      }

      return { meetingId: acceptedMeetingId, threadId, zoomReady: false };
    },
    onSuccess: (result, variables) => {
      // OPTIMISTIC UI: Clear proposals from cache immediately for instant UI update
      queryClient.setQueryData(["open-proposals-with-slots", variables.threadId], []);

      // Invalidate for eventual consistency
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["pending-meeting-proposal", variables.threadId] });

      // Show feedback based on Zoom status
      if (result.zoomReady) {
        toast.success("Meeting scheduled! ✓", {
          description: "Zoom link ready. Calendar invite sent!",
        });
      } else {
        toast.success("Meeting accepted! ✓", {
          description: "Your Zoom link will appear shortly.",
        });
      }
    },
    onError: (error, variables) => {
      const errorMessage = error instanceof Error ? error.message : String(error);
      if (errorMessage.includes("no longer open") || errorMessage.includes("already scheduled")) {
        toast.info("Meeting was already scheduled. Refreshing...");
        queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
        queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      } else if (errorMessage.includes("participants can accept")) {
        toast.error("Only meeting participants can accept this slot.");
      } else {
        toast.error("Failed to schedule meeting: " + errorMessage);
      }
    },
  });
}

// Cancel a scheduled meeting (also deletes the Zoom meeting if one exists)
export function useCancelScheduledMeeting() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      threadId,
      reason,
      zoomMeetingId,
      scheduledStartAt,
    }: {
      meetingId: string;
      threadId: string;
      reason?: string;
      zoomMeetingId?: string | null;
      scheduledStartAt?: string | null;
    }) => {
      // First, try to delete the Zoom meeting if one exists
      if (zoomMeetingId) {
        try {
          const deleteResponse = await supabase.functions.invoke("delete-zoom-meeting", {
            body: { meeting_id: zoomMeetingId },
          });
          if (deleteResponse.error) {
            console.error("Failed to delete Zoom meeting:", deleteResponse.error);
            // Continue with cancellation even if Zoom deletion fails
          }
        } catch (err) {
          console.error("Error calling delete-zoom-meeting:", err);
          // Continue with cancellation even if Zoom deletion fails
        }
      }

      // Then cancel in our database
      const { data, error } = await supabase.rpc("cancel_scheduled_meeting", {
        p_meeting_id: meetingId,
        p_reason: reason || null,
      });

      if (error) throw error;

      // Send cancellation calendar invite for future meetings (non-blocking)
      if (scheduledStartAt) {
        const scheduledStart = new Date(scheduledStartAt);
        const now = new Date();
        const twoMinutesFromNow = new Date(now.getTime() + 2 * 60 * 1000);

        // Only send cancellation invite if meeting was in the future
        if (scheduledStart > twoMinutesFromNow) {
          supabase.functions
            .invoke("send-calendar-invite", {
              body: { meetingId, action: "cancel" },
            })
            .then((res) => {
              if (res.error) {
                console.error("Failed to send cancellation invite:", res.error);
              } else {
                console.log("Cancellation invite sent:", res.data);
              }
            })
            .catch((err) => {
              console.error("Error sending cancellation invite:", err);
            });
        }
      }

      return data;
    },
    onSuccess: (_, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["pending-meeting-proposal", variables.threadId] });
      toast.success("Meeting cancelled.");
    },
    onError: (error) => {
      toast.error("Failed to cancel meeting: " + error.message);
    },
  });
}

// Log a join event
export function useLogMeetingJoin() {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      meetingId,
      source = "chat_card",
    }: {
      meetingId: string;
      source?: string;
    }) => {
      const { data, error } = await supabase.rpc("log_meeting_join", {
        p_meeting_id: meetingId,
        p_source: source,
      });

      if (error) {
        console.error("Failed to log join event:", error);
        // Don't throw - join logging is not critical
        return null;
      }
      return data;
    },
    onSuccess: () => {
      // Silently refresh join events
      queryClient.invalidateQueries({ queryKey: ["meeting-join-events"] });
    },
  });
}

// Get join events for a meeting (admin/analytics)
export function useMeetingJoinEvents(meetingId?: string) {
  return useQuery({
    queryKey: ["meeting-join-events", meetingId],
    queryFn: async () => {
      if (!meetingId) return [];

      const { data, error } = await supabase
        .from("meeting_join_events")
        .select("*")
        .eq("meeting_id", meetingId)
        .order("joined_at", { ascending: false });

      if (error) throw error;
      return data as MeetingJoinEvent[];
    },
    enabled: !!meetingId,
  });
}

// Check if there's an upcoming scheduled meeting
export function useUpcomingScheduledMeeting(threadId?: string) {
  return useQuery({
    queryKey: ["upcoming-scheduled-meeting", threadId],
    queryFn: async () => {
      if (!threadId) return null;

      const { data, error } = await supabase
        .from("mentorship_meetings")
        .select("*")
        .eq("thread_id", threadId)
        .in("status", SCHEDULED_MEETING_STATUSES)
        .gt("scheduled_start_at", new Date().toISOString())
        .order("scheduled_start_at", { ascending: true })
        .limit(1)
        .maybeSingle();

      if (error) throw error;
      return data as ScheduledMeetingWithDetails | null;
    },
    enabled: !!threadId,
  });
}

// Decline or cancel a meeting proposal - ATOMIC via RPC
// Uses the new decline_meeting_proposal RPC for atomic state transitions
export function useDeclineProposal() {
  const { user } = useAuth();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      proposalId,
      threadId,
      meetingId,
      reason,
      proposedByName,
      isCancellation = false,
    }: {
      proposalId: string;
      threadId: string;
      meetingId: string;
      reason?: string;
      proposedByName: string;
      isCancellation?: boolean;
    }) => {
      if (!user?.id) {
        throw new Error("Not authenticated");
      }

      console.log("[useDeclineProposal] Processing proposal via atomic RPC:", { 
        proposalId, threadId, meetingId, reason, isCancellation 
      });

      // Use the atomic RPC for decline/cancel
      const { error } = await supabase.rpc("decline_meeting_proposal", {
        p_proposal_id: proposalId,
        p_reason: reason || null,
        p_is_cancellation: isCancellation,
      });

      if (error) {
        console.error("[useDeclineProposal] RPC failed:", error);
        await logError({
          error: `Failed to ${isCancellation ? 'cancel' : 'decline'} proposal: ${error.message}`,
          component: "useDeclineProposal",
          action: "decline_meeting_proposal_rpc",
          metadata: { proposalId, threadId, errorCode: error.code, isCancellation },
        });
        throw new Error(`Failed to ${isCancellation ? 'cancel' : 'decline'} proposal: ${error.message}`);
      }

      // Update thread's updated_at
      await supabase
        .from("message_threads")
        .update({ updated_at: new Date().toISOString() })
        .eq("id", threadId);

      console.log("[useDeclineProposal] Proposal processed successfully via RPC", { isCancellation });
      return { proposalId, threadId, isCancellation };
    },
    onSuccess: (result, variables) => {
      queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["mentorship-meetings", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["pending-meeting-proposal", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["thread-messages", variables.threadId] });
      queryClient.invalidateQueries({ queryKey: ["message-threads"] });
      
      if (result.isCancellation) {
        toast.success("Proposal cancelled. You can propose new times.");
      } else {
        toast.success("Proposal declined. You can propose new times.");
      }
    },
    onError: (error, variables) => {
      const action = variables.isCancellation ? 'cancel' : 'decline';
      toast.error(`Failed to ${action} proposal: ` + error.message);
    },
  });
}

// ============================================================================
// REALTIME SUBSCRIPTION HOOK
// Subscribes to scheduled meeting tables for instant UI updates
// ============================================================================
export function useScheduledMeetingRealtime(threadId?: string) {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!threadId) return;

    console.log("[useScheduledMeetingRealtime] Setting up realtime for thread:", threadId);

    const channel = supabase
      .channel(`meeting-realtime-${threadId}`)
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mentorship_meetings",
          filter: `thread_id=eq.${threadId}`,
        },
        (payload) => {
          console.log("[Realtime] mentorship_meetings changed:", payload.eventType);
          queryClient.invalidateQueries({ queryKey: ["scheduled-meeting", threadId] });
          queryClient.invalidateQueries({ queryKey: ["upcoming-scheduled-meeting", threadId] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mentorship_meeting_proposals",
        },
        (payload) => {
          console.log("[Realtime] mentorship_meeting_proposals changed:", payload.eventType);
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
        }
      )
      .on(
        "postgres_changes",
        {
          event: "*",
          schema: "public",
          table: "mentorship_meeting_proposal_slots",
        },
        (payload) => {
          console.log("[Realtime] mentorship_meeting_proposal_slots changed:", payload.eventType);
          queryClient.invalidateQueries({ queryKey: ["open-proposals-with-slots", threadId] });
        }
      )
      .subscribe((status) => {
        console.log("[useScheduledMeetingRealtime] Subscription status:", status);
      });

    return () => {
      console.log("[useScheduledMeetingRealtime] Cleaning up realtime for thread:", threadId);
      supabase.removeChannel(channel);
    };
  }, [threadId, queryClient]);
}
